import { useState } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import {
  ACTIVITY_CATEGORIES,
  ACTIVITY_CATEGORY_KEYS,
  type ActivityCategoryKey,
} from '@l2u/shared/activityCategories';
import { adminApi } from '@l2u/ui/lib/api.ts';
import { Btn } from '@l2u/ui/components/Btn.tsx';
import { CenteredSpinner } from '@l2u/ui/components/Spinner.tsx';
import { EmptyState } from '@l2u/ui/components/EmptyState.tsx';
import { ErrorMessage } from '@l2u/ui/components/ErrorMessage.tsx';
import { Field, Select, TextInput } from '@l2u/ui/components/Field.tsx';
import { formatActivity } from '../lib/activityFormat.ts';
import { localDayEndIso, localDayStartIso } from '../lib/activityFilters.ts';
import type { ActivityEvent } from '@l2u/ui/lib/types.ts';

type ActivityPage = { events: ActivityEvent[]; next_cursor: number | null };

const PAGE_SIZE = 50;

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('en-AU', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

// Per-company activity table with server-side filters. Changing any filter
// changes the query key, which resets useInfiniteQuery back to the first page —
// required because the cursor (before_id) is only meaningful within one
// filtered sequence.
export function CompanyActivityTab({ companyId }: { companyId: string }) {
  const [category, setCategory] = useState<'' | ActivityCategoryKey>('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const hasFilters = category !== '' || fromDate !== '' || toDate !== '';

  const query = useInfiniteQuery({
    queryKey: ['company', companyId, 'activity', category, fromDate, toDate],
    queryFn: ({ pageParam }) =>
      adminApi.get<ActivityPage>('/api/activity', {
        query: {
          company_id: companyId,
          limit: PAGE_SIZE,
          before_id: pageParam ?? undefined,
          category: category || undefined,
          from: fromDate ? localDayStartIso(fromDate) : undefined,
          to: toDate ? localDayEndIso(toDate) : undefined,
        },
      }),
    initialPageParam: null as number | null,
    getNextPageParam: (last) => last.next_cursor,
  });

  const events = query.data?.pages.flatMap((p) => p.events) ?? [];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <div className="w-56">
          <Field label="Category">
            <Select
              value={category}
              onChange={(e) => setCategory(e.target.value as '' | ActivityCategoryKey)}
            >
              <option value="">All activity</option>
              {ACTIVITY_CATEGORY_KEYS.map((key) => (
                <option key={key} value={key}>
                  {ACTIVITY_CATEGORIES[key].label}
                </option>
              ))}
            </Select>
          </Field>
        </div>
        <div className="w-44">
          <Field label="From">
            <TextInput
              type="date"
              value={fromDate}
              max={toDate || undefined}
              onChange={(e) => setFromDate(e.target.value)}
            />
          </Field>
        </div>
        <div className="w-44">
          <Field label="To">
            <TextInput
              type="date"
              value={toDate}
              min={fromDate || undefined}
              onChange={(e) => setToDate(e.target.value)}
            />
          </Field>
        </div>
        {hasFilters && (
          <Btn
            size="sm"
            variant="secondary"
            onClick={() => {
              setCategory('');
              setFromDate('');
              setToDate('');
            }}
          >
            Clear
          </Btn>
        )}
      </div>

      {query.isPending ? (
        <CenteredSpinner />
      ) : query.error && events.length === 0 ? (
        <ErrorMessage error={query.error} />
      ) : events.length === 0 ? (
        hasFilters ? (
          <EmptyState
            title="No activity matches these filters"
            description="Try widening the date range or clearing the category."
          />
        ) : (
          <EmptyState title="No activity yet" />
        )
      ) : (
        <>
          {query.error != null && <ErrorMessage error={query.error} />}
          <div className="overflow-hidden rounded-lg border border-slate-200">
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-50 text-xs font-medium uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-4 py-2">Date</th>
                  <th className="px-4 py-2">Actor</th>
                  <th className="px-4 py-2">Event</th>
                  <th className="px-4 py-2">Detail</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {events.map((e) => {
                  const { title, detail, actor } = formatActivity(e);
                  return (
                    <tr key={e.id} className="align-top">
                      <td className="whitespace-nowrap px-4 py-2 text-slate-500">
                        {formatDateTime(e.occurred_at)}
                      </td>
                      <td className="px-4 py-2 text-slate-700">{actor}</td>
                      <td className="px-4 py-2 font-medium text-slate-900" title={e.event_type}>
                        {title}
                      </td>
                      <td className="px-4 py-2 text-slate-600">{detail ?? ''}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {query.hasNextPage && (
            <div className="text-center">
              <Btn
                variant="secondary"
                loading={query.isFetchingNextPage}
                onClick={() => void query.fetchNextPage()}
              >
                Load more
              </Btn>
            </div>
          )}
        </>
      )}
    </div>
  );
}
